import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import { useColors, useResponsive } from '@inbody/core';
import { useAuth } from '@inbody/keycloak';
import {
  LayoutHeader,
  LayoutSideMenuV2,
  MENU_COLLAPSED_WIDTH,
  MENU_WIDTH,
  useMenuStore,
} from '@inbody/ui';
import type { ExpandMenuItemType, MenuItemType } from '@inbody/ui';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { useInternalApiSystemsQuery } from '../../pages/internal-api/api';
import type { MeMenu, MeMenuCategory } from '../../pages/user-permission/api';
import { useMyMenusQuery } from '../../pages/user-permission/api';
import { Loading } from '../components/loading';
import { Urls } from '../lib/react-router/Urls';

// 백엔드 path 를 라우터 내부 이동으로 처리되도록 앞 슬래시만 보정.
const normalizeMenuUrl = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

// 카테고리와 그 하위 카테고리에 속한 모든 메뉴를 정렬해 평탄하게 모은다.
const collectMenus = (category: MeMenuCategory): MeMenu[] => {
  const bySortOrder = (a: { sortOrder: number }, b: { sortOrder: number }) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  const direct = [...category.menus].sort(bySortOrder);
  const nested = [...(category.children ?? [])]
    .sort(bySortOrder)
    .flatMap(collectMenus);
  return [...direct, ...nested];
};

const menusToLinks = (menus: MeMenu[]): MenuItemType[] =>
  menus
    .filter((menu) => !!menu.path)
    .map((menu) => ({
      id: `data-view-menu-${menu.menuId}`,
      type: 'link',
      label: menu.name,
      url: normalizeMenuUrl(menu.path),
      target: '_self',
    }));

/**
 * 하위(2단계) 카테고리 → 중첩 expand 노드.
 * 사이드메뉴가 3단계(카테고리 > 하위 카테고리 > 메뉴)까지만 렌더하므로,
 * 이보다 더 깊은 카테고리의 메뉴는 이 노드 아래로 모아 평탄화한다.
 */
const buildSubCategoryNode = (
  category: MeMenuCategory,
  keyPrefix: string,
): ExpandMenuItemType | null => {
  const links = menusToLinks(collectMenus(category));
  if (links.length === 0) return null;
  return {
    id: `data-view-category-${category.categoryId ?? keyPrefix}`,
    type: 'expand',
    label: category.name,
    children: links,
  };
};

/**
 * 최상위(1단계) 카테고리 → expand 노드.
 * 직속 메뉴는 link, 하위 카테고리는 중첩 expand 로 그대로 노출한다.
 * 표시할 메뉴가 하나도 없으면 카테고리 자체를 제외(null).
 */
const buildTopCategoryNode = (
  category: MeMenuCategory,
  keyPrefix: string,
): ExpandMenuItemType | null => {
  const bySortOrder = (a: MeMenuCategory, b: MeMenuCategory) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

  const directLinks = menusToLinks(
    [...category.menus].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
  );
  const subCategories = [...(category.children ?? [])]
    .sort(bySortOrder)
    .map((child, index) =>
      buildSubCategoryNode(
        child,
        `${keyPrefix}-${child.categoryId ?? `c${index}`}`,
      ),
    )
    .filter((node): node is ExpandMenuItemType => node !== null);

  const children = [...directLinks, ...subCategories];
  if (children.length === 0) return null;

  return {
    id: `data-view-category-${category.categoryId ?? keyPrefix}`,
    type: 'expand',
    label: category.name,
    iconNode: <QueryStatsOutlinedIcon />,
    children,
  };
};

// 기본으로 펼쳐둘 expand 노드 id 를 트리에서 모두 모은다.
const collectExpandIds = (items: MenuItemType[]): string[] =>
  items.flatMap((item) =>
    item.type === 'expand' ? [item.id, ...collectExpandIds(item.children)] : [],
  );

export const GenericLayout = () => {
  const { gray } = useColors();
  const location = useLocation();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // GET /api/v1/me/menus — 내가 접근 가능한 데이터 조회 메뉴만 카테고리별로 그린다.
  const { data: myMenus } = useMyMenusQuery();

  // 계층형 카테고리 트리를 최상위 메뉴 그룹으로 변환한다.
  // 최상위 카테고리(자금·채무 등)가 곧 사이드메뉴 최상위 그룹이 되고,
  // 하위 카테고리는 그 아래 중첩 expand 로 노출된다.
  const dataViewGroups: MenuItemType[] = useMemo(() => {
    if (!myMenus) return [];
    return [...myMenus]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((category, index) =>
        buildTopCategoryNode(
          category,
          `${category.categoryId ?? `root${index}`}`,
        ),
      )
      .filter((node): node is ExpandMenuItemType => node !== null);
  }, [myMenus]);

  // GET /api/v1/internal-apis — 사내 시스템마다 사이드바 'API > 사내 API' 아래 하나씩.
  // 시스템이 늘면 코드 수정 없이 백엔드 설정만으로 메뉴가 생긴다. 볼 수 없는 사용자(403)면 묶음이 숨는다.
  const { data: internalApiSystems } = useInternalApiSystemsQuery();
  const internalApiGroups: MenuItemType[] = useMemo(
    () =>
      internalApiSystems && internalApiSystems.length > 0
        ? [
            {
              id: 'internal-api',
              type: 'expand',
              label: '사내 API',
              children: internalApiSystems.map((system) => ({
                id: `internal-api-${system.code}`,
                type: 'link' as const,
                label: system.name,
                url: Urls.internalApi.build(system.code),
                target: '_self' as const,
              })),
            },
          ]
        : [],
    [internalApiSystems],
  );

  const menuItems: MenuItemType[] = useMemo(
    () => [
      ...dataViewGroups,
      {
        id: 'api',
        type: 'expand',
        label: 'API',
        iconNode: <CodeOutlinedIcon />,
        children: [
          {
            id: 'api-keys',
            type: 'link',
            label: 'API 키',
            url: Urls.apiKeys.path,
            target: '_self',
          },
          {
            id: 'api-catalog',
            type: 'link',
            label: 'D365 API',
            url: Urls.apiCatalog.path,
            target: '_self',
          },
          // 사이드메뉴는 링크 다음에 하위 묶음을 그린다. 시스템이 늘어도 API 키가 밀려나지 않는다.
          ...internalApiGroups,
        ],
      },
      {
        id: 'operation-management',
        type: 'expand',
        label: '운영 설정',
        iconNode: <AdminPanelSettingsOutlinedIcon />,
        requireRoleList: ['ADMIN'],
        children: [
          {
            id: 'menu-management',
            type: 'link',
            label: '메뉴 관리',
            url: Urls.menuManagement.path,
            target: '_self',
          },
          {
            id: 'user-permission',
            type: 'link',
            label: '사용자 역할 부여',
            url: Urls.userPermission.path,
            target: '_self',
          },
          {
            id: 'permission-management',
            type: 'link',
            label: '역할 관리',
            url: Urls.permissionManagement.path,
            target: '_self',
          },
          {
            id: 'api-key-overview',
            type: 'link',
            label: 'API 키 현황',
            url: Urls.apiKeyOverview.path,
            target: '_self',
          },
        ],
      },
    ],
    [dataViewGroups, internalApiGroups],
  );

  const initializeExpandedMenus = useMenuStore(
    (state) => state.initializeExpandedMenus,
  );

  useEffect(() => {
    initializeExpandedMenus([
      ...collectExpandIds(dataViewGroups),
      ...collectExpandIds(internalApiGroups),
      'api',
      'operation-management',
    ]);
  }, [initializeExpandedMenus, dataViewGroups, internalApiGroups]);

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // 현재 경로 기준으로 breadcrumb trail 계산 (메뉴 트리를 순회하며 현재 경로 탐색)
  const breadcrumbTrail = useMemo((): string[] => {
    const findTrail = (items: MenuItemType[]): string[] | null => {
      for (const item of items) {
        if (
          item.type === 'link' &&
          item.url &&
          item.url.startsWith('/') &&
          location.pathname.startsWith(item.url)
        ) {
          return [item.label];
        }
        if (item.type === 'expand') {
          const childTrail = findTrail(item.children);
          if (childTrail) return [item.label, ...childTrail];
        }
      }
      return null;
    };
    return findTrail(menuItems) ?? [];
  }, [menuItems, location.pathname]);

  const breadcrumbSlot = breadcrumbTrail.length > 0 && (
    <Breadcrumbs
      separator={<NavigateNextIcon sx={{ fontSize: 14, color: gray[400] }} />}
      aria-label="breadcrumb"
      sx={{
        '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
        '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' },
        '& .MuiBreadcrumbs-separator': {
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      <HomeIcon sx={{ fontSize: 14, color: gray[400], display: 'block' }} />
      {breadcrumbTrail.map((label, index) => {
        const isLast = index === breadcrumbTrail.length - 1;
        return (
          <Typography
            key={`${label}-${index}`}
            variant="caption"
            sx={{
              fontSize: 12,
              lineHeight: 1,
              color: isLast ? gray[700] : gray[500],
              fontWeight: isLast ? 500 : 400,
            }}
          >
            {label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: gray[25] }}>
      <LayoutSideMenuV2
        menuItemsList={menuItems}
        appTitle="Data"
        appSubtitle="사내 데이터 센터"
        headerLayout="horizontal"
        onCollapsedChange={setIsMenuCollapsed}
        mobileOpen={mobileMenuOpen}
        onMobileClose={handleMobileMenuClose}
      />
      <Box
        id="main-content"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: isMobile
            ? 0
            : isMenuCollapsed
              ? `${MENU_COLLAPSED_WIDTH}px`
              : `${MENU_WIDTH}px`,
          transition: 'margin-left 0.3s ease',
          overflow: 'hidden',
        }}
      >
        <LayoutHeader
          showLanguageChange={false}
          showUserSearch={false}
          showCorporateChange={false}
          accountMenuShowProfile
          userDisplayName={user?.firstName || ''}
          leftActions={breadcrumbSlot}
          onMenuToggle={handleMobileMenuToggle}
        />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Container sx={{ mt: 1, mx: 'auto', p: 2 }}>
            <Suspense fallback={<Loading page />}>
              <Outlet />
            </Suspense>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};
